/**
 * useQuery hook for fetching story content blocks.
 *
 * Replaces `storyViewEffects.getStoryContents` (compose-app). Uses the same
 * GraphQL queries (`getStoryContentsById` / `getTemplateContentsById`) and
 * applies the same ordering pipeline (`sortStoryContents` → `orderStoryContents`).
 *
 * Auth: `postQuery` (cookie/session auth — non-admin reachable, §1).
 *
 * Pass `null` to disable during route transitions before `entityId > 0` (§9).
 */

import { useQuery } from '@tanstack/react-query';
import {
  getStoryContentsById,
  getTemplateContentsById,
  processStoryContentOrTemplateContent,
} from '@opengov/stories-shared/graphQL';
import { IStoryContent } from '@opengov/stories-shared/types';
import { orderStoryContents, sortStoryContents } from '@opengov/stories-shared/utils';

import { postQuery } from '../../../effects/publications/postQuery';
import { useToast } from '../../Toast/context/useToast.context';
import { storyViewQueryKeys, StoryContentsKeyParams } from './storyViewQueryKeys';

export type UseStoryContentsParams = StoryContentsKeyParams | null;

export function useStoryContents(params: UseStoryContentsParams, errorMessage: string) {
  const { toggleToast } = useToast();

  return useQuery<IStoryContent[], Error>({
    queryKey: params
      ? storyViewQueryKeys.storyContents(params)
      : ([...storyViewQueryKeys.all, 'storyContents', 'disabled'] as const),
    queryFn: async () => {
      if (!params) throw new Error('Disabled');
      const { storyId, entityId, isTemplate } = params;

      const variables: Record<string, string | number> = {
        [isTemplate ? 'storyTemplateId' : 'storyId']: storyId,
        limit: 100,
        ...(!isTemplate && { entityId }),
      };

      const query = isTemplate ? getTemplateContentsById : getStoryContentsById;
      const data = await postQuery(query, variables);

      // `postQuery` returns `response.data`; story contents live at `.storyContents`
      const raw: IStoryContent[] = (data.storyContents ?? []).map(
        processStoryContentOrTemplateContent
      );
      return orderStoryContents(sortStoryContents(raw));
    },
    enabled: params !== null,
    onError: () => {
      toggleToast({ title: errorMessage, severity: 'error' });
    },
  });
}
